import React from 'react'
import { RouteContext } from '.'
import { FilterType } from '../filter-manager'

export const SMD_SESSION_FILTER_TYPE: FilterType = 'applications-vulns'

// clear session filters when navigating away from applcations route
export const shouldClearSessionFilters = (prevHash, nextHash) => {
  const [prevAppUuid, nextAppUuid] = [prevHash, nextHash].map(
    (it) => it.split('/')[3]
  )

  return prevAppUuid !== nextAppUuid
}

// used for grid components to consume the active session filters
export const useSessionFiltersSync = (
  targetFilterType?: FilterType,
  filterType: FilterType = SMD_SESSION_FILTER_TYPE
) => {
  const { loadSessionFilters } = React.useContext(RouteContext)

  React.useEffect(() => {
    loadSessionFilters(filterType, targetFilterType)
    // run once on init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

// This is used to support session metadata filtering
// this could exist in any root context
// it is attached to RouteContext presently since it
// deals with and uses function related to filtering that propagate from route
export const useSessionFilters = ({ setQueryParameters }) => {
  const [isMostRecentSession, setIsMostRecentSession] = React.useState(false)
  const [sessionFilters, setSessionFilters] = React.useState(
    new Map<string, Map<string, string[] | null>>()
  )

  const clearSessionFilters = React.useCallback(() => {
    setIsMostRecentSession(false)
    setSessionFilters(new Map<string, Map<string, string[]>>())
  }, [setIsMostRecentSession, setSessionFilters])

  const addSessionFilters = React.useCallback(
    (filterType: FilterType, filters: Map<string, string[]>) => {
      // copy sessionFilters
      const newSessionFilters = new Map(sessionFilters)

      const existingFilters =
        newSessionFilters.get(filterType) || new Map<string, string[]>()

      newSessionFilters.set(
        filterType,
        new Map([...existingFilters, ...filters])
      )

      setSessionFilters(newSessionFilters)
    },
    [setSessionFilters, sessionFilters]
  )

  const removeSessionFilters = React.useCallback(
    (filterType: FilterType, filters: Map<string, string[] | null>) => {
      const newSessionFilters = new Map(sessionFilters)
      const filtersMap = new Map(newSessionFilters.get(filterType))

      filters.forEach((_, key) => {
        if (filtersMap.get(key)) {
          filtersMap.set(key, null) // needed to clear the filter from the UI
        }
      })

      newSessionFilters.set(filterType, filtersMap)

      setSessionFilters(newSessionFilters)
    },
    [setSessionFilters, sessionFilters]
  )

  const loadSessionFilters = React.useCallback(
    (filterType: FilterType, filterTypeTarget?: FilterType) => {
      const filters =
        sessionFilters.get(filterType) || new Map<string, string[]>()

      setQueryParameters(filterTypeTarget || filterType, filters)
    },
    [setQueryParameters, sessionFilters]
  )

  return {
    // methods
    addSessionFilters,
    removeSessionFilters,
    loadSessionFilters,
    clearSessionFilters,
    setIsMostRecentSession,
    // data
    isMostRecentSession,
    sessionFilters
  }
}
